import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom, take } from 'rxjs';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  AppDialogRef,
  AppDialogService,
  AppModelModalComponent,
  AuthorizationService,
  Column,
  CustomAction,
  DataTableComponent,
  DateTimePipe,
  EmptyTableComponent,
  NimmstaDataSource,
  Worker,
  WorkerStatus,
} from '@nimmsta/ng-commons-lib';
import { WorkerFormComponent } from './worker-form.component';
import { WorkerStatusPipe } from './status.pipe';
import { PairUnpairAction } from './pairUnpairAction';
import { WorkerService } from '../../services/worker.service';
import { PairWorkerModalComponent } from '../../components/pair-worker-modal/pair-worker-modal.component';
import { WorkerUsesDraftPipe } from './usesDraft.pipe';

_('worker.title');
_('worker.nav');

@Component({
  selector: 'app-worker',
  templateUrl: 'worker.component.html',
  imports: [
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    WorkerFormComponent,
    TranslatePipe,
    TranslatePipe,
  ],
  providers: [WorkerStatusPipe, WorkerUsesDraftPipe, DateTimePipe],
})
export class WorkerComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private workerService = inject(WorkerService);
  private translate = inject(TranslateService);
  protected modalService = inject(AppDialogService);
  private statusBadge = inject(WorkerStatusPipe);
  private usesDraft = inject(WorkerUsesDraftPipe);

  private dateTimePipe = inject(DateTimePipe);

  workerSource!: NimmstaDataSource<Worker>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Worker>;

  @ViewChild('EditWorkerModal', { static: false })
  private editWorkerModalComponent?: AppModelModalComponent<Worker>;

  @ViewChild(WorkerFormComponent, { static: false })
  private workerFormComponent?: WorkerFormComponent;

  @ViewChild('unpairActiveWorkerQuestionModal', { static: true })
  unpairActiveWorkerQuestionModal!: TemplateRef<unknown>;

  customActions: CustomAction<Worker>[] = [];

  modalRef?: AppDialogRef;

  canWriteWorkers = false;

  readonly columns: Column[] = [
    new Column('family_name', 'user.family_name', { sortable: true }),
    new Column('given_name', 'user.given_name', { sortable: true }),
    new Column('status', 'user.status', { pipe: this.statusBadge }),
    new Column('createdAt', 'createdAt', {
      pipe: this.dateTimePipe,
      sortable: true,
    }),
    new Column('usesDraftWorkflowDefinition', 'user.uses_draft_definition', {
      pipe: this.usesDraft,
    }),
  ];

  ngOnInit(): void {
    this.canWriteWorkers =
      this.authorizationService.hasPermission('worker.write');
    this.workerSource = new NimmstaDataSource<Worker>(this.workerService);
    if (this.canWriteWorkers) {
      const pairAction = new PairUnpairAction();
      this.customActions.push(pairAction);
      pairAction.subscribe((id) => this.pairUnpair(id));
      const editAction = new CustomAction('edit');
      this.customActions.push(editAction);
      editAction.subscribe((id) => this.edit(id));
      const deleteAction = new CustomAction('delete', false, true);
      this.customActions.push(deleteAction);
      deleteAction.subscribe((id) => this.delete(id));
    }
  }

  new(): void {
    this.translate.get('worker.new').subscribe((value) => {
      this.openModal(value, true, {} as Worker);
    });
  }

  edit(id: string): void {
    this.translate.get('worker.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  pairUnpair(id: string): void {
    const worker = this.getSelectedObject(id)!;
    switch (worker.status) {
      case WorkerStatus.NOT_PAIRED:
        this.pair(worker);
        break;
      case WorkerStatus.PAIRED:
        this.unpair(worker);
        break;
      case WorkerStatus.ACTIVE:
        this.unpairActiveWorker(worker);
        break;
    }
  }

  pair(worker: Worker): void {
    if (this.modalRef !== undefined) {
      this.modalRef.close();
    }

    this.modalRef = this.modalService.open(PairWorkerModalComponent, {
      data: {
        worker,
      },
      size: 'sm',
    });
    this.modalRef.closed
      .pipe(take(1))
      .subscribe(() => this.tableComponent.loadDataPage());
  }

  unpair(worker: Worker): void {
    lastValueFrom(this.workerService.unpair(worker)).then(() =>
      this.tableComponent.loadDataPage(),
    );
  }

  unpairActiveWorker(worker: Worker): void {
    this.modalRef = this.modalService.open(
      this.unpairActiveWorkerQuestionModal,
    );
    this.modalRef.closed.pipe(take(1)).subscribe((data) => {
      if (data === 'unpair') {
        this.unpair(worker);
      }
    });
  }

  openModal(title: string, editable: boolean, worker: Worker): void {
    if (
      !this.canWriteWorkers ||
      !this.workerFormComponent ||
      !this.editWorkerModalComponent
    ) {
      return;
    }

    this.workerFormComponent.editable = editable;
    this.workerFormComponent.setValue(worker);
    this.editWorkerModalComponent.title = title;
    this.editWorkerModalComponent.editable = editable;
    this.editWorkerModalComponent.show('md');
  }

  delete(id: string): void {
    lastValueFrom(this.workerService.delete(this.getSelectedObject(id)!)).then(
      () => this.tableComponent.loadDataPage(),
    );
  }

  onSave(): void {
    this.workerFormComponent?.onSave();
  }

  onModalClose(): void {
    this.editWorkerModalComponent?.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): Worker | undefined {
    return this.workerSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    if (this.editWorkerModalComponent) {
      this.editWorkerModalComponent.loading = loading;
    }
  }
}
